// src/components/calendar/CalendarSearch.jsx
// ✅ 일정 검색 — 일/주/월/연 버튼 바 오른쪽.
//
// · 검색은 서버(/calendar/events/search/)가 한다 — 화면에 불러온 기간만 뒤지면 과거 일정이
//   안 나온다. 기간 제한·사이드바 필터와 무관하게 전체에서 찾는다.
// · 담당자 매칭은 서버가 저장된 이름 원본으로 한다. "(계약종료)"는 결과 표시에만 붙인다.
// · 입력 300ms 디바운스, 늦게 도착한 이전 응답은 버린다(reqId).
// · 바깥 클릭 판정은 mousedown 기준 — 입력칸 안에서 드래그하다 밖에서 손을 떼도 닫히지 않게.
import React, { useEffect, useRef, useState } from "react";
import api from "../../api";
import { EVENT_TYPE_COLORS } from "../../constants/eventTypes";
import { withEndedLabel } from "../../utils/contractEnded";

const DEBOUNCE_MS = 300;

const fmtWhen = (r) => {
  const date = String(r.start_date || "");
  const multi = r.end_date && String(r.end_date) !== date ? `~${String(r.end_date).slice(5)}` : "";
  const time = r.start_time ? ` ${String(r.start_time).slice(0, 5)}` : "";
  return `${date}${multi}${time}`;
};

export default function CalendarSearch({ onPick, onClear }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({ count: 0, results: [] });
  const wrapRef = useRef(null);
  const reqId = useRef(0);

  // 디바운스 검색
  useEffect(() => {
    const term = q.trim();
    if (!term) {
      setData({ count: 0, results: [] });
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const my = ++reqId.current;
    const t = setTimeout(() => {
      api.get("/calendar/events/search/", { params: { q: term } })
        .then((res) => { if (my === reqId.current) setData(res.data || { count: 0, results: [] }); })
        .catch(() => { if (my === reqId.current) setData({ count: 0, results: [] }); })
        .finally(() => { if (my === reqId.current) setLoading(false); });
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [q]);

  // 바깥 클릭 닫기
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const clear = () => {
    setQ("");
    setOpen(false);
    onClear?.();
  };

  const handleChange = (e) => {
    const v = e.target.value;
    setQ(v);
    setOpen(true);
    if (!v.trim()) onClear?.();   // 입력을 지우면 원래 화면으로
  };

  const more = Math.max(0, (data.count || 0) - data.results.length);

  return (
    <div ref={wrapRef} style={{ position: "relative", marginLeft: "auto", alignSelf: "center" }}>
      <div style={{
        display: "flex", alignItems: "center", gap: "6px", width: "280px", height: "34px",
        padding: "0 8px", border: "1px solid #dadce0", borderRadius: "6px", background: "#fff",
        boxSizing: "border-box",
      }}>
        <span aria-hidden style={{ fontSize: "14px", flex: "0 0 auto" }}>🔍</span>
        <input
          type="text"
          value={q}
          placeholder="일정 검색"
          onChange={handleChange}
          onFocus={() => q.trim() && setOpen(true)}
          onKeyDown={(e) => { if (e.key === "Escape") { setOpen(false); e.currentTarget.blur(); } }}
          style={{ flex: "1 1 auto", minWidth: 0, width: "100%", border: "none", outline: "none", fontSize: "14px", background: "transparent" }}
        />
        {q && (
          <button
            type="button"
            onClick={clear}
            title="검색 지우기"
            style={{
              width: "22px", height: "22px", minWidth: "22px", flex: "0 0 22px", padding: 0,
              border: "none", borderRadius: "50%", background: "#eee", color: "#666",
              cursor: "pointer", lineHeight: 1, fontSize: "13px",
            }}
          >
            ×
          </button>
        )}
      </div>

      {open && q.trim() && (
        <div style={{
          position: "absolute", top: "40px", right: 0, width: "560px", maxHeight: "440px",
          overflowY: "auto", background: "#fff", border: "1px solid #dadce0", borderRadius: "8px",
          boxShadow: "0 6px 20px rgba(0,0,0,0.15)", zIndex: 1000,
        }}>
          {loading && data.results.length === 0 ? (
            <div style={{ padding: "12px 14px", color: "#888", fontSize: "13px", whiteSpace: "nowrap" }}>검색 중…</div>
          ) : data.results.length === 0 ? (
            <div style={{ padding: "12px 14px", color: "#888", fontSize: "13px", whiteSpace: "nowrap" }}>검색 결과가 없습니다.</div>
          ) : (
            <>
              {data.results.map((r) => (
                <div
                  key={r.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => { setOpen(false); onPick?.(r); }}
                  onKeyDown={(e) => { if (e.key === "Enter") { setOpen(false); onPick?.(r); } }}
                  style={{
                    display: "flex", alignItems: "center", gap: "10px", padding: "8px 14px",
                    borderBottom: "1px solid #f1f3f4", cursor: "pointer", fontSize: "13px",
                    whiteSpace: "nowrap",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#f1f6fe"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = ""; }}
                >
                  <span style={{ flex: "0 0 150px", color: "#555" }}>{fmtWhen(r)}</span>
                  <span style={{ flex: "0 0 140px", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {withEndedLabel(r.assignee_display || "-", r.is_contract_ended)}
                  </span>
                  <span style={{ flex: "0 0 auto", display: "flex", alignItems: "center", gap: "4px", color: "#555" }}>
                    <span style={{
                      width: "9px", height: "9px", borderRadius: "50%", flex: "0 0 9px",
                      background: EVENT_TYPE_COLORS[r.event_type] || "#90caf9", border: "1px solid rgba(0,0,0,0.25)",
                    }} />
                    {r.event_type}
                  </span>
                  <span style={{ flex: "1 1 auto", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", fontWeight: 500 }}>
                    {r.title || "(제목 없음)"}
                  </span>
                </div>
              ))}
              {more > 0 && (
                <div style={{ padding: "8px 14px", color: "#888", fontSize: "12px", whiteSpace: "nowrap", background: "#fafafa" }}>
                  {more.toLocaleString()}건 더 있음 — 검색어를 더 입력해 좁혀 보세요
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
